import React, { useState, useLayoutEffect } from 'react';

/* Tutorial de bienvenida tipo "coach-mark": oscurece el fondo, ILUMINA un elemento
   REAL del menú (por selector CSS) y muestra un globo con LETRA GRANDE y pocas palabras
   (pensado para gente mayor). Pasos con "Siguiente" y "Saltar".
   steps: [{ sel: '#id-del-elemento', titulo, texto }] */

export default function TutorialBienvenida({ steps = [], onFinish = () => {}, onSkip = null }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const paso = steps[i];

  useLayoutEffect(() => {
    if (!paso) return undefined;
    const medir = () => {
      const el = document.querySelector(paso.sel);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      } else {
        setRect(null);
      }
    };
    medir();
    const t = setTimeout(medir, 350); // re-medir tras el scroll suave
    window.addEventListener('resize', medir);
    window.addEventListener('scroll', medir, true);
    return () => { clearTimeout(t); window.removeEventListener('resize', medir); window.removeEventListener('scroll', medir, true); };
  }, [paso]);

  if (!paso) return null;

  const pad = 8;
  const hole = rect ? { top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 } : null;

  // El globo se ancla AL BORDE del recuadro iluminado (no al borde de la pantalla):
  // se coloca en el lado —arriba o abajo del elemento— donde haya MÁS hueco, y
  // se limita su altura a ese hueco. Así NUNCA se solapa con el punto que explica.
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const GAP = 16;
  // 📱 MÓVIL (Ann 2026-07-26: «no es responsive, no se entiende nada»): nada de
  // anclar el globo al elemento (quedaba estrujado con scroll). En pantallas
  // pequeñas el globo es una HOJA INFERIOR fija, ancha y con aire; el foco
  // sigue iluminando el elemento (centrado por el scrollIntoView).
  const esMovil = vw <= 640;
  let bubblePos;
  if (esMovil) {
    bubblePos = { left: 10, right: 10, bottom: 10, maxHeight: '55vh' };
  } else if (hole) {
    const espacioAbajo = vh - (hole.top + hole.height);
    const espacioArriba = hole.top;
    // Si en ninguno de los dos lados cabe con dignidad (≥260px), hoja abajo también.
    if (Math.max(espacioAbajo, espacioArriba) < 260) {
      bubblePos = { bottom: 16, maxHeight: '50vh' };
    } else if (espacioAbajo >= espacioArriba) {
      // más sitio debajo del elemento → globo debajo
      bubblePos = { top: hole.top + hole.height + GAP, maxHeight: Math.max(240, espacioAbajo - GAP * 2) };
    } else {
      // más sitio encima → globo encima
      bubblePos = { bottom: vh - hole.top + GAP, maxHeight: Math.max(240, espacioArriba - GAP * 2) };
    }
  } else {
    bubblePos = { bottom: 24, maxHeight: 'calc(100vh - 48px)' };
  }

  const ultimo = i + 1 >= steps.length;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10000, fontFamily: "'Montserrat', system-ui, sans-serif" }}>
      {/* Foco (spotlight) sobre el elemento real */}
      {hole ? (
        <div style={{ position: 'fixed', top: hole.top, left: hole.left, width: hole.width, height: hole.height, borderRadius: 16, boxShadow: '0 0 0 9999px rgba(10,25,28,.72)', border: '3px solid #7fd8b8', pointerEvents: 'none', transition: 'all .25s ease' }} />
      ) : (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,25,28,.72)' }} />
      )}

      {/* Globo en el lado contrario al elemento (no lo tapa) y totalmente visible. */}
      {/* Tarjeta COMPACTA y clara (Ann 2026-07-28: «muy grande, incómodo, la gente
          no sabe qué hacer»): puntitos de progreso, un texto corto y DOS botones
          en fila. Nada de globos gigantes. */}
      <div style={{ position: 'fixed', ...(esMovil ? {} : { left: '50%', transform: 'translateX(-50%)', width: 'min(92vw,400px)' }), ...bubblePos, overflowY: 'auto', background: '#fff', borderRadius: 16, padding: '16px 16px 12px', boxShadow: '0 16px 45px rgba(0,0,0,.4)' }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }} aria-label={(i + 1) + ' de ' + steps.length}>
          {steps.map((_, k) => (
            <span key={k} style={{ width: k === i ? 22 : 8, height: 8, borderRadius: 99, background: k === i ? '#2C6675' : '#d5e2e4', transition: 'all .25s' }} />
          ))}
        </div>
        <h2 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800, color: '#16323a', lineHeight: 1.2 }}>{paso.titulo}</h2>
        <p style={{ margin: '0 0 14px', fontSize: 15, color: '#2f4247', lineHeight: 1.45 }}>{paso.texto}</p>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button onClick={() => (onSkip ? onSkip() : onFinish())} style={{ flex: 'none', background: 'none', border: 'none', color: '#5a7176', fontSize: 15, cursor: 'pointer', fontFamily: 'inherit', padding: '10px 8px' }}>Saltar</button>
          <button
            onClick={() => (ultimo ? onFinish() : setI(i + 1))}
            style={{ flex: 1, height: 46, borderRadius: 12, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg,#3C8696,#2C6675)', color: '#fff', fontSize: 17, fontWeight: 800, fontFamily: 'inherit' }}
          >
            {ultimo ? '¡Comenzar!' : 'Siguiente →'}
          </button>
        </div>
      </div>
    </div>
  );
}
